import React from 'react';
import DemoStage from '../DemoStage.jsx';

/**
 * Animated walkthrough of Assume identity: open the user menu, enter the user's
 * email (their actual role is resolved server-side, never picked), reload as
 * that user (purple banner; the UI stays fully interactive but
 * finishing an action shows a notice), then Exit back to your own account. The
 * banner colour, wording and dialog fields mirror ImpersonationControl.jsx
 * (#7c3aed, "Viewing as … · actions disabled") so the demo matches what
 * actually appears. Placeholder data only.
 */

const STEPS = [
  {
    caption: 'Open the user menu at the bottom of the sidebar and choose Assume identity.',
    scene: 'menu', ms: 2400,
    cursor: 'menu-item', click: true,
  },
  {
    caption: 'Enter the user’s email — their actual role and clinic or region scope follow that email.',
    scene: 'dialog', ms: 2600,
    cursor: 'confirm',
  },
  {
    caption: 'Assume identity reloads the app as that user.',
    scene: 'dialog', ms: 1500,
    cursor: 'confirm', click: true,
  },
  {
    caption: 'The purple banner stays on every page — everything looks and opens as it does for that user, but nothing can be saved or posted.',
    scene: 'assumed', ms: 3000,
    cursor: 'edit',
  },
  {
    caption: 'Exit is always available on the banner.',
    scene: 'assumed', ms: 1800,
    cursor: 'exit', click: true,
  },
  {
    caption: 'Banner gone — you are back on your own SuperAdmin account.',
    scene: 'back', ms: 2600,
  },
];

function MenuItem({ children, target, active }) {
  return (
    <div
      data-kb-target={target}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 10px', borderRadius: 6, fontSize: 12,
        fontWeight: active ? 600 : 400,
        color: active ? 'var(--text)' : 'var(--text-2)',
        background: active ? 'var(--surface-2)' : 'transparent',
      }}
    >
      {children}
    </div>
  );
}

export default function ImpersonationDemo() {
  return (
    <DemoStage steps={STEPS} label="Assuming another user's identity" height={260}>
      {(step) => {
        const scene = step?.scene ?? 'menu';
        const assumed = scene === 'assumed';
        return (
          <div className="kb-mock" style={{ maxWidth: 480 }}>
            {/* Read-only banner — slides in while assuming */}
            {assumed && (
              <div
                className="kb-pop"
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                  padding: '7px 12px', borderRadius: 8, marginBottom: 10,
                  fontSize: 11.5, background: '#7c3aed', color: '#fff',
                }}
              >
                <strong>Viewing as clinicmanager@therapieclinic.com</strong>
                <span style={{ opacity: 0.85 }}>· Clinic Manager · actions disabled</span>
                <span style={{ flex: 1 }} />
                <button
                  type="button" tabIndex={-1} data-kb-target="exit"
                  style={{
                    background: 'rgba(255,255,255,0.18)', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.35)', borderRadius: 6,
                    padding: '3px 10px', fontSize: 11, fontWeight: 600,
                  }}
                >
                  Exit
                </button>
              </div>
            )}

            {/* Mock case toolbar — controls stay fully visible while assuming;
                only finishing an action is blocked (with a notice) */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 12px', borderRadius: 10,
              border: '1px solid var(--border)', background: 'var(--surface)',
            }}>
              <span className="badge event-type">AE-042</span>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Clinic_A · Code 2 · Open</span>
              <span style={{ flex: 1 }} />
              <button
                type="button" className="btn sm" tabIndex={-1} data-kb-target="edit"
                style={{ fontSize: 11 }}
              >
                Edit
              </button>
              <button
                type="button" className="btn primary sm" tabIndex={-1}
                style={{ fontSize: 11 }}
              >
                Post comment
              </button>
            </div>

            {/* User-menu popover */}
            {scene === 'menu' && (
              <div className="kb-pop" style={{
                marginTop: 10, maxWidth: 220, padding: 6, borderRadius: 10,
                border: '1px solid var(--border)', background: 'var(--surface)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }}>
                <MenuItem>Admin Panel</MenuItem>
                <MenuItem target="menu-item" active>Assume identity…</MenuItem>
                <MenuItem>Lock now</MenuItem>
                <MenuItem>Sign out</MenuItem>
              </div>
            )}

            {/* Assume-identity dialog */}
            {scene === 'dialog' && (
              <div className="kb-pop" style={{
                marginTop: 10, maxWidth: 320, padding: 12, borderRadius: 10,
                border: '1px solid var(--border)', background: 'var(--surface)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>Assume identity</div>
                <div className="kb-mock-label">User email</div>
                <div className="kb-mock-input" style={{ fontSize: 12, minHeight: 28 }}>clinicmanager@therapieclinic.com</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                  <button type="button" className="btn ghost sm" tabIndex={-1} style={{ fontSize: 11 }}>Cancel</button>
                  <button type="button" className="btn primary sm" tabIndex={-1} data-kb-target="confirm" style={{ fontSize: 11 }}>
                    Assume identity
                  </button>
                </div>
              </div>
            )}

            {/* Sidebar user card */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <span className="kb-mock-avatar">MA</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>M. Adams</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Super Admin</div>
              </div>
            </div>
          </div>
        );
      }}
    </DemoStage>
  );
}
